import { Button, Typography } from "@hellocarbo/lasagne";
import { Calendar, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";

import { TCampaignDuration } from "@/types";
import {
  calculateNextCampaignEndDate,
  formatEndDate,
} from "@/utils/formatDate";

type ConfirmationProps = {
  startDate: Date;
  duration: TCampaignDuration;
  loading?: boolean;
  errorMessage?: string;
  onBackButtonClick: () => void;
};

export const DURATION_IN_MONTHS = {
  [TCampaignDuration.threeMonths]: 3,
  [TCampaignDuration.sixMonths]: 6,
  [TCampaignDuration.twelveMonths]: 12,
};

function Confirmation({
  startDate,
  duration,
  loading = false,
  errorMessage,
  onBackButtonClick,
}: ConfirmationProps) {
  const { t } = useTranslation(["common", "formatter", "trackingCampaign"]);

  const endDate = calculateNextCampaignEndDate(
    startDate,
    DURATION_IN_MONTHS[duration]
  );

  return (
    <div>
      <div className="gap-y-xs flex flex-col">
        <div className="gap-x-2xs flex items-center">
          <Calendar className="size-md" />
          <Typography>
            {t("trackingCampaign:coveredPeriod")}
            <Typography as="span" weight="bold" className="ml-3xs text-primary">
              {t("formatter:date", {
                value: startDate,
              })}
              &nbsp;-&nbsp;
              {t("formatter:date", {
                value: formatEndDate(endDate),
              })}
            </Typography>
          </Typography>
        </div>
        <div className="gap-x-2xs flex items-center">
          <Clock className="size-md" />
          <Typography>
            {t("trackingCampaign:duration")}
            <Typography as="span" weight="bold" className="ml-3xs text-primary">
              {t(`trackingCampaign:durations.${duration}`)}
            </Typography>
          </Typography>
        </div>
      </div>

      <div className="mt-lg text-center">
        {errorMessage ? (
          <div className="mb-lg bg-negative-xx-light p-2xs rounded-md text-start">
            <Typography className="text-grayscale-dark">
              {t(errorMessage)}
            </Typography>
          </div>
        ) : (
          <Typography weight="semibold" className="mb-sm">
            {t("trackingCampaign:doYouWantToStartFootprint")}
          </Typography>
        )}
        <div className="gap-x-sm flex items-center justify-center">
          <Button shape="link" onClick={onBackButtonClick}>
            {t("common:edit")}
          </Button>
          <Button type="submit" isLoading={loading}>
            {t("trackingCampaign:startMyFootprint")}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default Confirmation;
